import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, Tabs, TabsList, TabsTrigger, TabsContent } from '@devkit/ui';
import { Binary, RefreshCw } from 'lucide-react';
import { trackToolExecute, trackCopy, trackClear } from '@devkit/analytics';

export default function Base64Page() {
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleEncode = useCallback(() => {
    setError(null);
    try {
      const encoded = btoa(unescape(encodeURIComponent(input)));
      setOutput(encoded);
      trackToolExecute('base64', 'encode');
    } catch (e) {
      setError('Failed to encode. Please check your input.');
      setOutput('');
    }
  }, [input]);

  const handleDecode = useCallback(() => {
    setError(null);
    try {
      const decoded = decodeURIComponent(escape(atob(input)));
      setOutput(decoded);
      trackToolExecute('base64', 'decode');
    } catch (e) {
      setError('Invalid Base64 string. Please check your input.');
      setOutput('');
    }
  }, [input]);

  const handleSwap = useCallback(() => {
    setInput(output);
    setOutput('');
    setMode(mode === 'encode' ? 'decode' : 'encode');
  }, [output, mode]);

  const clearAll = useCallback(() => {
    setInput('');
    setOutput('');
    setError(null);
    trackClear('base64');
  }, []);

  const handleProcess = () => {
    if (mode === 'encode') {
      handleEncode();
    } else {
      handleDecode();
    }
  };

  return (
    <ToolLayout slug="base64">
      <Card>
        <CardContent className="p-6">
          {/* Mode Tabs */}
          <Tabs value={mode} onValueChange={(v) => { setMode(v as 'encode' | 'decode'); setError(null); }}>
            <TabsList>
              <TabsTrigger value="encode">Encode</TabsTrigger>
              <TabsTrigger value="decode">Decode</TabsTrigger>
            </TabsList>

            <TabsContent value="encode">
              <p className="text-sm text-slate-400 mb-4">
                Convert plain text to Base64 encoded string
              </p>
            </TabsContent>

            <TabsContent value="decode">
              <p className="text-sm text-slate-400 mb-4">
                Convert Base64 string back to plain text
              </p>
            </TabsContent>
          </Tabs>

          {/* Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {mode === 'encode' ? 'Plain Text' : 'Base64 String'}
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={mode === 'encode' ? 'Enter text to encode...' : 'Enter Base64 string to decode...'}
              className="w-full h-40 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 mb-4">
            <Button onClick={handleProcess} className="gap-2">
              <Binary className="w-4 h-4" />
              {mode === 'encode' ? 'Encode' : 'Decode'}
            </Button>
            <Button variant="outline" onClick={handleSwap} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Swap
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          {/* Output */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-300">
                {mode === 'encode' ? 'Base64 Output' : 'Decoded Text'}
              </label>
              {output && (
                <CopyButton text={output} onCopy={() => trackCopy('base64')} />
              )}
            </div>
            <textarea
              value={error ? '' : output}
              readOnly
              placeholder={error ? '' : 'Result will appear here...'}
              className={`w-full h-40 px-4 py-3 bg-surface border rounded-lg font-mono text-sm text-slate-100 resize-y ${
                error ? 'border-red-500' : 'border-slate-700'
              }`}
            />
            {error && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      </ToolLayout>
  );
}
